/* import __COLOCATED_TEMPLATE__ from './content-status.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import { type StatusCounts } from 'embercom/lib/content-service/search-api';
import type PALETTE from '@intercom/pulse/lib/palette';
import { type StatusDotState } from './status-dot';

interface Signature {
  Element: HTMLDivElement;
  Args: {
    statusCounts?: StatusCounts;
  };
}

export default class ContentStatusComponent extends Component<Signature> {
  @service declare intl: IntlService;

  get hasData(): boolean {
    return !!this.args.statusCounts;
  }

  get total(): number {
    return this.args.statusCounts?.total ?? 0;
  }

  get hasCountableData(): boolean {
    return this.hasData && (this.total > 0 || this.isSyncing);
  }

  get passedCount(): number {
    return this.args.statusCounts?.passed_count ?? 0;
  }

  get failedCount(): number {
    return this.args.statusCounts?.failed_count ?? 0;
  }

  get excludedCount(): number {
    return this.args.statusCounts?.excluded_count ?? 0;
  }

  get isSyncing(): boolean {
    return this.args.statusCounts?.status === 'syncing';
  }

  get hasMajorityFailures(): boolean {
    return this.failedCount > this.total / 2;
  }

  get syncStatus(): StatusDotState {
    if (this.isSyncing) {
      return 'syncing';
    }

    if (this.hasMajorityFailures) {
      return 'error';
    }

    return 'success';
  }

  get statusDotClasses(): string {
    if (this.isSyncing) {
      return 'border-4 bg-notice-fill-emphasis border-notice-container';
    }

    if (this.hasMajorityFailures) {
      return 'border-4 bg-error-fill-emphasis border-error-container';
    }

    return 'border-4 bg-success-fill-emphasis border-success-container';
  }

  get stampColor(): keyof typeof PALETTE {
    if (this.hasMajorityFailures) {
      return 'text-error';
    }
    return 'text-success';
  }

  get stampText(): string {
    if (this.isSyncing) {
      return this.intl.t('knowledge-hub.filterable-list.list.cells.content-status.syncing');
    }

    if (this.total === 0) {
      return '';
    }

    if (this.hasMajorityFailures) {
      return this.intl.t(
        'knowledge-hub.filterable-list.list.cells.content-status.majority-failed',
        {
          failed: this.failedCount,
          total: this.total,
        },
      );
    }

    // All passed case
    if (this.failedCount === 0 && this.excludedCount === 0) {
      return this.intl.t('knowledge-hub.filterable-list.list.cells.content-status.all-live', {
        live: this.passedCount,
      });
    }

    // Some passed, not majority failed
    return this.intl.t('knowledge-hub.filterable-list.list.cells.content-status.some-failed', {
      live: this.passedCount,
      total: this.total,
    });
  }

  get shouldShowTooltipContent(): boolean {
    return this.hasData && (this.failedCount > 0 || this.excludedCount > 0);
  }

  get failedText(): string {
    if (this.failedCount > 0) {
      return `${this.failedCount} ${this.intl.t('knowledge-hub.filterable-list.list.cells.content-status.failed')}`;
    }
    return '';
  }

  get passedText(): string {
    if (this.passedCount > 0) {
      return `${this.passedCount} ${this.intl.t('knowledge-hub.filterable-list.list.cells.content-status.live')}`;
    }
    return '';
  }

  get excludedText(): string {
    if (this.excludedCount > 0) {
      return `${this.excludedCount} ${this.intl.t('knowledge-hub.filterable-list.list.cells.content-status.excluded')}`;
    }
    return '';
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::FilterableList::Table::Cells::ContentStatus': typeof ContentStatusComponent;
    'knowledge-hub/filterable-list/table/cells/content-status': typeof ContentStatusComponent;
  }
}
